import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MatDialogConfig,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { AsyncPipe } from '@angular/common';
import { Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import {
  AgencyActions,
  AppState,
  ItemActions,
  SchemaEditorActions,
  StepperDataPayload,
  StepperDataService,
  selectSchemaViewBySid,
} from '@qtek/libs/store';
import { Item, ItemTypeBitMasks, NdaWorkflow } from '@qtek/shared/models';
import { Observable, of } from 'rxjs';
import { filter, first, map, take } from 'rxjs/operators';

export const closedDealStepperDialogConfig: MatDialogConfig = {
  panelClass: ['qt-dialog', 'closed-deal-stepper-dialog'],
  width: '800px',
  maxWidth: '95vw',
  maxHeight: '95vh',
};

export interface ClosedDealFormValue {
  setup: {
    transactionType: string;
    dealName: string;
    facilityId: string;
    numLenders: number;
    loanPurpose: string;
  };
  structure: {
    productType: string;
    rateType: string;
    amount: number;
    term: number;
    amortization: number;
    closedDate: Date | null;
    maturityDate: Date | null;
  };
  borrower: {
    industry: string;
    revenueSize: string;
    peSponsored: boolean;
  };
  pricing: {
    prepayment: string;
    fixedRate: number | null;
    spread: number | null;
    index: string;
    floor: number | null;
  };
  collateral: {
    collateralType: string;
    guarantor: string;
  };
  cre: {
    propertyClass: string;
    projectType: string;
    ltv: number | null;
    ltc: number | null;
    noiRatio: number | null;
  };
  covenants: {
    financialCovenants: string[];
    originationFee: number | null;
    hedge: boolean;
    performancePricing: boolean;
  };
}

interface SchemaEnumItem {
  id: string;
  label?: string;
  description?: string;
  name?: string;
}

const CRE_PRODUCT_TYPES_PATTERN = /construction/i;
const CRE_LOAN_PURPOSES = ['CRE Acquisition', 'CRE Construction'];
const STEPPER_NAME = 'closedDealStepper';

// Schema SID constants (mirrors backend SchemaService)
const SID_COMMON = 'io.synply.v1.model.core.common';
const SID_FEES = 'io.synply.v1.model.core.fees';
const SID_BORROWER = 'io.synply.v1.model.core.borrower';
const SID_COLLATERAL = 'io.synply.v1.model.core.collateral';
const SID_IDENTIFICATORS = 'io.synply.v1.model.core.identificators';
const SID_GUARANTORS = 'io.synply.v1.model.core.guarantors';
const SID_AGENCY = 'io.synply.v1.model.core.agency';
const SID_PARTICIPATION = 'io.synply.v1.model.derived.participation';
const SID_PRODUCT_TERM = 'io.synply.v1.model.product.term';

@Component({
  selector: 'qt-closed-deal-stepper-dialog',
  templateUrl: './closed-deal-stepper-dialog.component.html',
  styleUrls: ['./closed-deal-stepper-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatDatepickerModule,
    MatNativeDateModule,
    TranslationCoreModule,
  ],
})
export class ClosedDealStepperDialogComponent implements OnInit, AfterViewInit {
  private destroyRef = inject(DestroyRef);
  private dialogRef = inject(MatDialogRef<ClosedDealStepperDialogComponent>);
  private el = inject(ElementRef);
  private stepperDataService = inject(StepperDataService);
  private store = inject(Store<AppState>);
  private actions$ = inject(Actions);
  private router = inject(Router);

  @ViewChild('stepper') stepper!: MatStepper;

  // Options loaded from backend schema
  productTypes$: Observable<SchemaEnumItem[]> = of([]);
  industries$: Observable<SchemaEnumItem[]> = of([]);

  readonly transactionTypes = [
    'New Credit',
    'Renewal',
    'Refinance',
    'Amendment',
    'Participation',
  ];

  readonly loanPurposes = [
    'Working Capital',
    'Acquisition',
    'Real Estate',
    'Equipment',
    'CRE Acquisition',
    'CRE Construction',
    'Other',
  ];

  readonly rateTypes = ['Fixed', 'Floating'];

  readonly revenueSizes = [
    '< $10M',
    '$10M - $50M',
    '$50M - $250M',
    '$250M - $1B',
    '> $1B',
  ];

  readonly prepaymentOptions = [
    'None',
    'Yield Maintenance',
    'Step-Down',
    'Defeasance',
    'Make-Whole',
  ];

  readonly rateIndices = ['SOFR', 'Prime', 'Fed Funds', 'LIBOR', 'Other'];

  readonly collateralTypes = [
    'Real Estate',
    'Equipment',
    'Accounts Receivable',
    'Inventory',
    'Cash',
    'Other',
  ];

  readonly propertyClasses = ['Class A', 'Class B', 'Class C'];

  readonly projectTypes = [
    'Office',
    'Retail',
    'Industrial',
    'Multifamily',
    'Hospitality',
    'Mixed Use',
    'Other',
  ];

  readonly financialCovenantOptions = [
    { key: 'leverage', label: 'Leverage Ratio' },
    { key: 'debtService', label: 'Debt Service Coverage' },
    { key: 'currentRatio', label: 'Current Ratio' },
    { key: 'fixedCharge', label: 'Fixed Charge Coverage' },
    { key: 'tangibleNetWorth', label: 'Tangible Net Worth' },
    { key: 'minLiquidity', label: 'Minimum Liquidity' },
  ];

  setupForm = new FormGroup({
    transactionType: new FormControl<string>('', Validators.required),
    dealName: new FormControl<string>('', Validators.required),
    facilityId: new FormControl<string>(''),
    numLenders: new FormControl<number>(1, [
      Validators.required,
      Validators.min(1),
    ]),
    loanPurpose: new FormControl<string>('', Validators.required),
  });

  structureForm = new FormGroup({
    productType: new FormControl<string>('', Validators.required),
    rateType: new FormControl<string>('', Validators.required),
    amount: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(0),
    ]),
    term: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(1),
    ]),
    amortization: new FormControl<number | null>(null),
    closedDate: new FormControl<Date | null>(null, Validators.required),
    maturityDate: new FormControl<Date | null>(null, Validators.required),
  });

  borrowerForm = new FormGroup({
    industry: new FormControl<string>('', Validators.required),
    revenueSize: new FormControl<string>('', Validators.required),
    peSponsored: new FormControl<boolean>(false),
  });

  pricingForm = new FormGroup({
    prepayment: new FormControl<string>('', Validators.required),
    fixedRate: new FormControl<number | null>(null),
    spread: new FormControl<number | null>(null),
    index: new FormControl<string>(''),
    floor: new FormControl<number | null>(null),
  });

  collateralForm = new FormGroup({
    collateralType: new FormControl<string>('', Validators.required),
    guarantor: new FormControl<string>(''),
  });

  creForm = new FormGroup({
    propertyClass: new FormControl<string>('', Validators.required),
    projectType: new FormControl<string>('', Validators.required),
    ltv: new FormControl<number | null>(null, [
      Validators.min(0),
      Validators.max(100),
    ]),
    ltc: new FormControl<number | null>(null, [
      Validators.min(0),
      Validators.max(100),
    ]),
    noiRatio: new FormControl<number | null>(null, Validators.min(0)),
  });

  covenantsForm = new FormGroup({
    financialCovenants: new FormArray(
      this.financialCovenantOptions.map(() => new FormControl<boolean>(false))
    ),
    originationFee: new FormControl<number | null>(null, Validators.min(0)),
    hedge: new FormControl<boolean>(false),
    performancePricing: new FormControl<boolean>(false),
  });

  isFloatingRate = signal(false);
  showCreStep = signal(false);
  isSaving = signal(false);

  private draftExists = false;
  private stepperReady = false;
  private stepToRestore: number | null = null;

  get financialCovenantsArray(): FormArray {
    return this.covenantsForm.get('financialCovenants') as FormArray;
  }

  ngAfterViewInit(): void {
    this.stepperReady = true;
    this.updateConnectorLines();
    this.tryRestoreStep();
  }

  onStepChange(event: StepperSelectionEvent): void {
    this.updateConnectorLines(event.selectedIndex);
    this.saveDraft(event.selectedIndex);
  }

  private updateConnectorLines(
    activeIndex = this.stepper?.selectedIndex ?? 0
  ): void {
    const dark = 'var(--app-main-text)';
    const gray = 'var(--app-frame-border)';

    const headers: HTMLElement[] = Array.from(
      this.el.nativeElement.querySelectorAll('.mat-horizontal-stepper-header')
    );
    headers.forEach((header, i) => {
      header.style.setProperty(
        '--qt-step-after-color',
        i < activeIndex ? dark : gray
      );
      header.style.setProperty(
        '--qt-step-before-color',
        i <= activeIndex ? dark : gray
      );
    });

    const lines: HTMLElement[] = Array.from(
      this.el.nativeElement.querySelectorAll('.mat-stepper-horizontal-line')
    );
    lines.forEach((line, i) => {
      line.style.setProperty(
        'border-top-color',
        i < activeIndex ? dark : gray,
        'important'
      );
    });
  }

  ngOnInit(): void {
    this.loadSchemaOptions();

    this.structureForm
      .get('rateType')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(val => {
        this.isFloatingRate.set(val === 'Floating');
        this.updatePricingValidators(val === 'Floating');
      });

    this.setupForm
      .get('loanPurpose')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateCreVisibility());

    this.structureForm
      .get('productType')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateCreVisibility());

    this.loadDraft();

    // Save draft whenever the dialog is dismissed without submitting
    // (covers X button, Cancel, backdrop click)
    this.dialogRef
      .beforeClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.isSaving()) {
          this.saveDraft(this.stepper?.selectedIndex ?? 0, true);
        }
      });
  }

  private loadSchemaOptions(): void {
    this.store.dispatch(
      SchemaEditorActions.getSchema({ sid: 'io.synply.v1.type.product' })
    );
    this.store.dispatch(
      SchemaEditorActions.getSchema({ sid: 'io.synply.v1.type.indu' })
    );

    this.productTypes$ = this.store
      .select(selectSchemaViewBySid('io.synply.v1.type.product'))
      .pipe(
        filter(schema => !!schema),
        map(({ graphEnum }) =>
          Object.entries(graphEnum.vals).map(([id, val]: [string, any]) => ({
            id,
            ...val,
          }))
        ),
        first()
      );

    this.industries$ = this.store
      .select(selectSchemaViewBySid('io.synply.v1.type.indu'))
      .pipe(
        filter(schema => !!schema),
        map(({ graphEnum }) =>
          Object.entries(graphEnum.vals).map(([id, val]: [string, any]) => ({
            id,
            ...val,
          }))
        ),
        first()
      );
  }

  private loadDraft(): void {
    this.stepperDataService
      .getStepperData(STEPPER_NAME)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          if (response?.res) {
            this.draftExists = true;
            this.stepToRestore = response.res.step;
            try {
              const data: Partial<ClosedDealFormValue> = JSON.parse(
                response.res.jsonData
              );
              this.restoreForms(data);
            } catch {
              // ignore parse errors, start fresh
            }
            this.tryRestoreStep();
          }
        },
        error: () => {
          // no draft exists, start fresh
        },
      });
  }

  private restoreForms(data: Partial<ClosedDealFormValue>): void {
    if (data.setup) {
      this.setupForm.patchValue(data.setup);
    }
    if (data.structure) {
      this.structureForm.patchValue({
        ...data.structure,
        closedDate: data.structure.closedDate
          ? new Date(data.structure.closedDate)
          : null,
        maturityDate: data.structure.maturityDate
          ? new Date(data.structure.maturityDate)
          : null,
      });
    }
    if (data.borrower) {
      this.borrowerForm.patchValue(data.borrower);
    }
    if (data.pricing) {
      this.pricingForm.patchValue(data.pricing);
    }
    if (data.collateral) {
      this.collateralForm.patchValue(data.collateral);
    }
    if (data.cre) {
      this.creForm.patchValue(data.cre);
    }
    if (data.covenants) {
      const { financialCovenants, ...rest } = data.covenants;
      this.covenantsForm.patchValue(rest);
      if (financialCovenants) {
        this.financialCovenantsArray.controls.forEach((ctrl, i) => {
          ctrl.setValue(
            financialCovenants.includes(this.financialCovenantOptions[i].key)
          );
        });
      }
    }
  }

  private tryRestoreStep(): void {
    if (
      this.stepperReady &&
      this.stepToRestore !== null &&
      this.stepToRestore > 0
    ) {
      const target = this.stepToRestore;
      this.stepToRestore = null;
      this.stepper.linear = false;
      this.stepper.selectedIndex = target;
      this.stepper.linear = true;
      this.updateConnectorLines(target);
    }
  }

  private buildDraftPayload(step: number): StepperDataPayload {
    const selectedCovenants = this.financialCovenantOptions
      .filter((_, i) => this.financialCovenantsArray.at(i).value)
      .map(o => o.key);

    return {
      stepperName: STEPPER_NAME,
      step,
      jsonData: JSON.stringify({
        setup: this.setupForm.getRawValue(),
        structure: this.structureForm.getRawValue(),
        borrower: this.borrowerForm.getRawValue(),
        pricing: this.pricingForm.getRawValue(),
        collateral: this.collateralForm.getRawValue(),
        cre: this.creForm.getRawValue(),
        covenants: {
          ...this.covenantsForm.getRawValue(),
          financialCovenants: selectedCovenants,
        },
      }),
    };
  }

  private saveDraft(step: number, keepAlive = false): void {
    const payload = this.buildDraftPayload(step);

    const request$ = this.draftExists
      ? this.stepperDataService.updateStepperData(payload)
      : this.stepperDataService.createStepperData(payload);

    // keepAlive=true skips takeUntilDestroyed so the request survives component destruction
    const piped$ = keepAlive
      ? request$
      : request$.pipe(takeUntilDestroyed(this.destroyRef));

    piped$.subscribe({
      next: () => {
        this.draftExists = true;
      },
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      error: () => {},
    });
  }

  private updateCreVisibility(): void {
    const productType = this.structureForm.get('productType')!.value ?? '';
    const loanPurpose = this.setupForm.get('loanPurpose')!.value ?? '';
    const show =
      CRE_PRODUCT_TYPES_PATTERN.test(productType) ||
      CRE_LOAN_PURPOSES.includes(loanPurpose);
    this.showCreStep.set(show);
  }

  private updatePricingValidators(isFloating: boolean): void {
    const fixedRateCtrl = this.pricingForm.get('fixedRate')!;
    const spreadCtrl = this.pricingForm.get('spread')!;
    const indexCtrl = this.pricingForm.get('index')!;

    if (isFloating) {
      fixedRateCtrl.clearValidators();
      spreadCtrl.setValidators([Validators.required]);
      indexCtrl.setValidators([Validators.required]);
    } else {
      fixedRateCtrl.setValidators([Validators.required, Validators.min(0)]);
      spreadCtrl.clearValidators();
      indexCtrl.clearValidators();
    }

    fixedRateCtrl.updateValueAndValidity();
    spreadCtrl.updateValueAndValidity();
    indexCtrl.updateValueAndValidity();
  }

  onCancel(): void {
    // Keep the draft so the user can resume later
    this.dialogRef.close();
  }

  onSubmit(): void {
    if (!this.isFormValid()) return;

    this.isSaving.set(true);

    const setup = this.setupForm.getRawValue();
    const structure = this.structureForm.getRawValue();
    const borrower = this.borrowerForm.getRawValue();
    const pricing = this.pricingForm.getRawValue();
    const collateral = this.collateralForm.getRawValue();
    const cre = this.creForm.getRawValue();
    const selectedCovenants = this.financialCovenantOptions
      .filter((_, i) => this.financialCovenantsArray.at(i).value)
      .map(o => o.key);
    const covenants = {
      ...this.covenantsForm.getRawValue(),
      financialCovenants: selectedCovenants,
    };

    const item: Item = { ...new Item() };
    item.name = setup.dealName;
    item.stg = 500; // Agency stage — auto-publishes to benchmark pool server-side
    item.prTp = ItemTypeBitMasks.LOAN;
    item.ndaWorkFlow = NdaWorkflow.None;
    item.myPar = {
      sts: 7,
      role: 1000,
      committedVal: { a: structure.amount ?? 0, c: 'USD' },
    };
    item.data = {
      sid: {
        a: SID_AGENCY,
        c: SID_COMMON,
        f: SID_FEES,
        b: SID_BORROWER,
        col: SID_COLLATERAL,
        id: SID_IDENTIFICATORS,
        g: SID_GUARANTORS,
        l: SID_PRODUCT_TERM,
        s: structure.productType, // product schema ID from store
        i: borrower.industry, // industry schema ID from store
        p: SID_PARTICIPATION,
      },
      data: {
        // Setup
        'c.loanPurpose': setup.loanPurpose,
        'c.numberOfLenders': setup.numLenders,
        'id.facilityId': setup.facilityId,
        // Structure
        'c.pr': structure.productType,
        'c.oAmt': structure.amount,
        'c.trm': structure.term,
        'c.amtz': structure.amortization,
        'c.cDt': structure.closedDate?.toISOString().split('T')[0] ?? null,
        'c.matDt': structure.maturityDate?.toISOString().split('T')[0] ?? null,
        'f.interestRateType': structure.rateType,
        // Borrower
        'c.indu': borrower.industry,
        'b.borrowerRevenueSize': borrower.revenueSize,
        'b.privateEquitySponsored': String(borrower.peSponsored),
        // Pricing
        'c.sprd': pricing.spread,
        'c.initInrRte': pricing.fixedRate,
        'c.indx': pricing.index,
        'f.prepaymentPenaltyFee': pricing.prepayment,
        'f.whatIsTheFloorRate': pricing.floor,
        // Collateral
        'col.collateralType': collateral.collateralType,
        'g.guarantorType': collateral.guarantor,
        // CRE
        'c.crePropertyClass': cre.propertyClass,
        'c.projectType': cre.projectType,
        'l.ltv': cre.ltv,
        'col.loanToCost': cre.ltc,
        'c.noiRatio': cre.noiRatio,
        // Covenants
        'f.origFee': covenants.originationFee,
        'f.interestRateHedge': String(covenants.hedge),
        'f.performanceBasedPricing': String(covenants.performancePricing),
        'f.financialCovenants': covenants.financialCovenants.join(','),
      },
    };

    // Listen for success then delete draft and navigate.
    // Both actions are checked because the WebSocket response fires whichever
    // action corresponds to the active feed subscription.
    this.actions$
      .pipe(
        ofType(AgencyActions.createItemSuccess, ItemActions.createItemSuccess),
        filter(({ res }) => res.name === setup.dealName),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ res }) => {
        if (this.draftExists) {
          this.stepperDataService
            .deleteStepperData(STEPPER_NAME)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
        }
        this.dialogRef.close();
        this.isSaving.set(false);
        this.router.navigate(['itms/agency', 'deal', btoa(res._id!)], {
          queryParams: { prevUrl: this.router.url },
        });
      });

    // Reset saving state on failure
    this.actions$
      .pipe(
        ofType(AgencyActions.createItemFailure),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.isSaving.set(false));

    this.store.dispatch(AgencyActions.createItem({ payload: item }));
  }

  isFormValid(): boolean {
    return (
      this.setupForm.valid &&
      this.structureForm.valid &&
      this.borrowerForm.valid &&
      this.pricingForm.valid &&
      this.collateralForm.valid &&
      (!this.showCreStep() || this.creForm.valid)
    );
  }
}
